import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useScroll } from "framer-motion";
import { Heart, Instagram, Facebook, Youtube, Menu, X, Search, User, ShoppingBag, Sun, Moon } from "lucide-react";
import { useConsultation } from "./Consultation";
import { useTheme } from "./Theme";
import { EASE } from "./Shared";

const NAV = [
  { to: "/shop", label: "SHOP" },
  { to: "/collections", label: "COLLECTIONS" },
  { to: "/bespoke", label: "BESPOKE" },
  { to: "/measure", label: "MEASURE" },
  { to: "/designer", label: "ABOUT" },
  { to: "/journal", label: "JOURNAL" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const { open } = useConsultation();
  const { theme, toggle } = useTheme();
  const location = useLocation();

  useEffect(() => scrollY.on("change", (v) => setScrolled(v > 60)), [scrollY]);
  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <>
      <motion.header
        data-testid="site-header"
        className={`always-dark fixed inset-x-0 top-0 z-[80] transition-all duration-500 ${
          scrolled ? "border-b border-line/10 bg-ink/85 backdrop-blur-xl" : "bg-transparent"
        }`}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.6, ease: EASE }}
      >
        <div className={`mx-auto flex max-w-[1600px] items-center justify-between px-5 transition-all duration-500 sm:px-10 ${scrolled ? "py-3" : "py-6"}`}>
          <Link to="/" data-testid="header-logo-link" className="relative z-10">
            <img src="/images/logo-light-bg.png" alt="Rutu Bhonsle" className={`w-auto transition-all duration-500 ${scrolled ? "h-16" : "h-24"}`} />
          </Link>

          <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 2xl:gap-10 xl:flex" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group relative font-mono text-[12px] 2xl:text-[13px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                    isActive ? "text-flame" : "text-bone/80 hover:text-bone"
                  }`
                }
              >
                {n.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-full origin-right scale-x-0 bg-flame transition-transform duration-500 group-hover:origin-left group-hover:scale-x-100" />
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4 sm:gap-6">
            <button type="button" data-testid="header-search-button" aria-label="Search" className="hidden text-bone/70 transition-colors hover:text-flame sm:block">
              <Search className="h-5 w-5" />
            </button>
            <button type="button" data-testid="header-account-button" aria-label="Account" className="hidden text-bone/70 transition-colors hover:text-flame sm:block">
              <User className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={toggle}
              data-testid="theme-toggle-button"
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              className="text-bone/70 transition-colors hover:text-flame"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button type="button" data-testid="header-cart-button" aria-label="Cart" className="relative text-bone/70 transition-colors hover:text-flame">
              <ShoppingBag className="h-5 w-5" />
              <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-flame font-mono text-[9px] text-ink">0</span>
            </button>
            <button
              type="button"
              onClick={open}
              data-testid="header-book-consultation-button"
              className="hidden border border-flame px-7 py-3.5 font-mono text-xs uppercase tracking-[0.25em] text-flame transition-all duration-300 hover:bg-flame hover:text-ink md:block"
            >
              Book Consultation
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className="text-bone xl:hidden"
            >
              <Menu className="h-7 w-7" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[110] flex flex-col bg-ink"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: EASE }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-6">
              <span>
                <img src="/images/logo-light-bg.png" alt="Rutu Bhonsle" className="logo-for-dark h-20 w-auto" />
                <img src="/images/logo-dark-bg.png" alt="Rutu Bhonsle" className="logo-for-light h-20 w-auto" />
              </span>
              <div className="flex items-center gap-6">
                <button type="button" onClick={toggle} data-testid="mobile-theme-toggle-button" aria-label="Toggle theme" className="text-bone transition-colors hover:text-flame">
                  {theme === "dark" ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />}
                </button>
                <button type="button" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close-button" aria-label="Close menu" className="text-bone">
                  <X className="h-7 w-7" />
                </button>
              </div>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {[...NAV, { to: "/", label: "HOME" }].map((n, i) => (
                <span key={n.to} className="mask-line">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.7, delay: 0.15 + i * 0.07, ease: EASE }}
                  >
                    <NavLink
                      to={n.to}
                      data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                      className="font-serif text-5xl font-light text-bone transition-colors duration-300 hover:text-flame"
                    >
                      {n.label}
                    </NavLink>
                  </motion.span>
                </span>
              ))}
              <motion.button
                type="button"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                onClick={() => { setMenuOpen(false); open(); }}
                data-testid="mobile-menu-consultation-button"
                className="mt-10 w-fit border border-flame px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-flame"
              >
                Book a Consultation
              </motion.button>
            </nav>
            <div className="px-8 pb-10 font-mono text-[10px] uppercase tracking-[0.3em] text-fog">@rutubhonsle — Nagpur · Mumbai · New York</div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const FOOTER_NAV = {
  HOUSE: [
    { to: "/shop", label: "Shop" },
    { to: "/collections", label: "Collections" },
    { to: "/bespoke", label: "Bespoke" },
    { to: "/designer", label: "About" },
    { to: "/journal", label: "Journal" },
  ],
  CARE: [
    { to: "/", label: "Shipping" },
    { to: "/", label: "Returns" },
    { to: "/", label: "Size Guide" },
    { to: "/", label: "Care Guide" },
    { to: "/", label: "FAQ" },
  ],
};

export const Footer = () => {
  const { open } = useConsultation();
  return (
    <footer className="border-t border-line/10 bg-ink" data-testid="site-footer">
      <div className="mx-auto grid max-w-[1600px] gap-14 px-5 py-16 sm:px-10 md:grid-cols-[1.4fr_1fr_1fr_1fr] md:py-24">
        <div>
          <img src="/images/logo-light-bg.png" alt="Rutu Bhonsle" className="logo-for-dark h-28 w-auto" />
          <img src="/images/logo-dark-bg.png" alt="Rutu Bhonsle" className="logo-for-light h-28 w-auto" />
          <p className="mt-6 max-w-xs text-sm font-light leading-relaxed text-fog">
            Contemporary Indian fashion house. Bespoke couture, bridal and ready-to-wear — crafted with a modern point of view.
          </p>
          <div className="mt-8 flex items-center gap-4">
            {[
              { Icon: Instagram, label: "Instagram", href: "https://instagram.com" },
              { Icon: Facebook, label: "Facebook", href: "https://facebook.com" },
              { Icon: Youtube, label: "YouTube", href: "https://youtube.com" },
            ].map(({ Icon, label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={`footer-social-${label.toLowerCase()}`}
                aria-label={label}
                className="border border-line/15 p-3 text-fog transition-colors duration-300 hover:border-flame hover:text-flame"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
        {Object.entries(FOOTER_NAV).map(([title, links]) => (
          <div key={title}>
            <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.35em] text-fog">{title}</p>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm font-light text-bone/70 transition-colors duration-300 hover:text-flame">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div>
          <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.35em] text-fog">BEGIN</p>
          <p className="text-sm font-light leading-relaxed text-bone/70">Let's create something that feels unmistakably you.</p>
          <button
            type="button"
            onClick={open}
            data-testid="footer-book-consultation-button"
            className="mt-6 border border-flame px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-flame transition-all duration-300 hover:bg-flame hover:text-ink"
          >
            Book a Consultation
          </button>
        </div>
      </div>
      <div className="border-t border-line/10">
        <div className="mx-auto flex max-w-[1600px] flex-col items-center justify-between gap-3 px-5 py-6 font-mono text-[10px] uppercase tracking-[0.3em] text-fog sm:flex-row sm:px-10">
          <span data-testid="footer-copyright">© RUTU BHONSLE</span>
          <span className="flex items-center gap-2">
            <Heart className="h-3 w-3 text-flame" /> Crafted in India. Designed for the world.
          </span>
        </div>
      </div>
    </footer>
  );
};
